/**
 * src/router.tsx
 *
 * TanStack Router config — code-defined route tree (no file-based gen
 * step). Auth is gated at the protected layout: any route under "/"
 * runs a `beforeLoad` guard that bounces to /login when no API key is
 * stored.
 *
 * Route tree:
 *   /login              — set + persist x-api-key
 *   /                   — Layout (sidebar + outlet)
 *     /                 → Instances (default landing page)
 *     /pools            → pool grid + capacity rollup
 *     /providers        → provider list + add-provider form
 *     /alerts           → infra.chrome alerts (live)
 *     /audit            → operator + agent action log
 *     /live/:instanceId → noVNC viewer + CDP action panel
 */

import {
  createRootRoute,
  createRoute,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import Layout from './components/Layout';
import Login from './pages/Login';
import Instances from './pages/Instances';
import Pools from './pages/Pools';
import Providers from './pages/Providers';
import Alerts from './pages/Alerts';
import Audit from './pages/Audit';
import Live from './pages/Live';

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: Login,
});

const teleportAuthRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/x-teleport-auth',
  beforeLoad: () => {
    throw redirect({ to: '/' });
  },
  component: () => null,
});

const protectedLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'protected',
  component: Layout,
});

const instancesRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/',
  component: Instances,
});

const poolsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/pools',
  component: Pools,
});

const providersRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/providers',
  component: Providers,
});

const alertsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/alerts',
  component: Alerts,
});

const auditRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/audit',
  component: Audit,
});

const liveRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/live/$instanceId',
  component: Live,
});

export const routeTree = rootRoute.addChildren([
  loginRoute,
  teleportAuthRoute,
  protectedLayoutRoute.addChildren([
    instancesRoute,
    poolsRoute,
    providersRoute,
    alertsRoute,
    auditRoute,
    liveRoute,
  ]),
]);
